import { ButtonType, Header, IconClose, IconGeotabDrive, IconNotificationAlarm, IconPeople, IconWifi2, IconWrench, ThemeDrive } from "@geotab/zenith";
import React, { useEffect, useState } from "react";
import UserDialogContent from "./components/UserDialog";
import NotificationDialog from "./components/NotificationDialog";
import ConnectionStatusDialog from "./components/ConnectionDialog";
import { useStoreContext } from "../../../../ui/framework/hooks/StoreContext";
import {
    getPageState,
    hidingIcons,
    isDoingDvir,
    isLogout,
    isShowingBackButton,
    isWorkflow
} from "./helpers/Helper";
import { DriverActionTypes, PageLevel } from "../../../../store/mobile/types";
import { getHomeHash, isDriverViewingInspectionMode } from "../../../../utils/uri";
import { useRouterContext } from "../../../../ui/framework/hooks/RouterContext";
import { goBack } from "../../../../utils/helpers";
import Spinner from "../../../../utils/spinner";
import { useApiContext } from "../../../../ui/framework/hooks/ApiContext";
import { useUserServiceContext } from "../../../../ui/framework/hooks/UserServiceContext";
import { DeviceMovementActions } from "../../../../store/deviceMovement/actions";
import User from "../../../../model/User";
import { useAppContext } from "../../../../ui/framework/hooks/AppViewContext";
import { MobileActions } from "../../../../store/mobile/actions";
import { clearTempAssetsFromState } from "../../../../ui/assets/helper";
import { dispatcher, DispatcherEvents } from "../../../../utils/dispatcher";
import { Source, error, log, Event as EventType, event as loggerEvent } from "../../../../utils/logger";
import { useStoreSelector } from "../../../../ui/framework/hooks/UseSelector";
import { useTranslationContext } from "../../../../ui/framework/hooks/TranslationContext";
import { BannerAlert } from "./components/Banner";
import { DisplayDetailEnum } from "./helpers/ConnectionHelper";
import Notifications from "../../../../utils/notifications";
import { TopBarTranslationsDictionary } from "./helpers/TopbarTranslation";
import { useToastContext } from "../../../../ui/framework/hooks/ToastContext";

const TopBarViewModules = () => {
    const { store } = useStoreContext();
    const { router } = useRouterContext();
    const { api } = useApiContext();
    const { userService } = useUserServiceContext();
    const { showInfoToast } = useToastContext();

    const { translate } = useTranslationContext();
    const { app } = useAppContext();

    const [userMobileSheet, setUserMobileSheet] = useState(false);
    const [notificationMobileSheet, setNotificationMobileSheet] = useState(false);
    const [connectionMobileSheet, setConnectionMobileSheet] = useState(false);
    const [currentUser, setCurrentUser] = useState<IUser>(new User(userService.getCurrentUser()));
    const [connectionDisplayDetail, setConnectionDisplayDetail] = useState<DisplayDetailEnum>(DisplayDetailEnum.default);
    const [notificationCount, setNotificationCount] = useState(0);

    // Reactively subscribe to store state changes
    const pageState = useStoreSelector(state => state.mobile.pageState.title);
    const users = useStoreSelector(state => state.activeUsers);
    const nightMode = useStoreSelector(state => state.data.nightMode);
    const messageSelector = useStoreSelector(state => state.messages);
    const debugMode = useStoreSelector(state => state.data.debugMode);

    const clearDriveLock = () => {
        if (userService.isDriverUsingUI(currentUser?.name)) {
            let currentDrivingStatus = store.getState().data.driving;
            let hadCachedContentView = !!app.cachedContentView;

            store.dispatch(DeviceMovementActions.setStateUpdate({
                isDriving: false,
                source: "USER_EVENT",
                loggingMessage: "User manually clear Lockscreen from login"
            }));

            log(Source.USER_EVENT, `LockScreen: Cleared by user, cachedContentView=${ hadCachedContentView }, wasDriving=${ currentDrivingStatus }`, currentUser);

            api.call("Add", {
                typeName: "Audit",
                entity: {
                    name: "DriveLockUserCleared",
                    comment: `Driving? ${ currentDrivingStatus }`
                }
            });
        }
    };

    const navigate = () => {
        if (!hidingIcons(store) && !isWorkflow(store)) {
            if (getPageState(store).isLocked) {
                let spinner = new Spinner();
                spinner.spin(document.getElementById("title-x-icon"));
                app.screenHandler.canClearLockScreen(false).then((result: boolean) => {
                    if (result) {
                        loggerEvent(EventType.MOTIONCHANGE, "Manual lock status: Executing", userService.getCurrentUser());
                        clearDriveLock();
                    } else {
                        loggerEvent(EventType.MOTIONCHANGE, "Manual lock status: Rejected", userService.getCurrentUser());
                        showInfoToast("Cannot interact with the ELD while the vehicle is in motion.");
                    }
                    spinner.stop();
                }).catch(e => {
                    spinner.stop();
                    error(Source.UI, `LockScreen error ${ e }`);
                    clearDriveLock();
                });
            } else if (isShowingBackButton(store)) {
                if (getPageState(store).pageLevel === PageLevel.SUBMENU_WITH_BACK_BUTTON) {
                    let previousPage = store.getState().mobile.previousPage;
                    if (previousPage) {
                        router.navigate(previousPage, true);
                    } else {
                        router.navigate(getHomeHash(), true);
                    }
                } else {
                    goBack();
                }
            }
        }
    };

    const cancelLoginLogout = () => {
        const pageLevel = getPageState(store).pageLevel;

        store.dispatch(MobileActions.setDriverActionNecessary({
            isNecessary: false,
            actionType: DriverActionTypes.Workflow
        }));
        clearTempAssetsFromState(store, userService);

        if (pageLevel === PageLevel.FORCE_LOGOUT) {
            // We don't do a session.close here because we haven't created any logs at this point, and therefore no need for us to set an OFF log
            loggerEvent(EventType.DEBUG_LOGINVIEW_NAVIGATE, `cancelLoginLogout button pressed`);
            router.navigate("login,(loggingOut:true)", true);
        } else if (pageLevel === PageLevel.RETURN_ASSETS) {
            router.navigate("assets/main", true);
        } else if (isDriverViewingInspectionMode()) {
            dispatcher.trigger(DispatcherEvents.ExitInspectionMode);
        } else {
            // Cancel the logout before the sync
            router.navigate(getHomeHash(), true);
        }
    };

    useEffect(() => {
        setCurrentUser(new User(userService.getCurrentUser()));
    }, [users]);

    useEffect(() => {
        setNotificationCount(Notifications.count(currentUser.id));
    }, [messageSelector]);

    const renderBackButton = () => {
        if (getPageState(store).isLocked) {
            return (
                <Header.Back id="title-x-icon" className="topbar-clear-lockscreen" onClick={navigate}>
                    <IconClose size="huge" />
                </Header.Back>
            );
        }
        return isShowingBackButton(store) ? <Header.Back id="title-back-icon" onClick={navigate} /> : undefined;
    };

    const renderLoginState = () => {
        return (
            <Header>
                <Header.Title key="1" pageName="" >
                    <div style={{ position: "absolute", left: "50%", transform: "translateX(-50%)", display: "flex", alignItems: "center", top: "50%", marginTop: "-12px" }}>
                        <IconGeotabDrive size="huger"/>
                    </div>
                </Header.Title>
            </Header>
        );
    };

    const renderAdditionalActionButton = () => {
        if (isDriverViewingInspectionMode()) {
            return (
                <Header.Button
                    icon={IconClose}
                    important={true}
                    title={translate(TopBarTranslationsDictionary.Exit)}
                    iconSize="huge"
                    id="topbar-driver-asset-selection-cancelbutton"
                    type={ButtonType.TertiaryBlack}
                    onClick={() => cancelLoginLogout()}
                >
                    {translate(TopBarTranslationsDictionary.Exit)}
                </Header.Button>
            );
        } else if (isLogout(store)) {
            return (
                <Header.Button
                    icon={IconClose}
                    important={true}
                    title={translate(TopBarTranslationsDictionary.Cancel)}
                    id="5"
                    iconSize="huge"
                    type={ButtonType.TertiaryBlack}
                    onClick={() => cancelLoginLogout()}
                >
                    {translate(TopBarTranslationsDictionary.Cancel)}
                </Header.Button>
            );
        }

        return <></>;
    };

    const renderActionButton = () => {
        if (hidingIcons(store)) {
            return <></>;
        }

        const canShowIcons = !isWorkflow(store) && !isDoingDvir(store) && !isDriverViewingInspectionMode();
        return (
            <>
                { canShowIcons ? <Header.Button iconSize="huge" key="connection" icon={IconWifi2} important={true} id="topbar-status" title={translate(TopBarTranslationsDictionary.Connection)} type={ButtonType.TertiaryBlack} onClick={() => setConnectionMobileSheet(true)} /> : undefined }
                { debugMode ? <Header.Button icon={IconWrench} iconSize="huge" important={true} id="topbar-debug" title={translate(TopBarTranslationsDictionary.DebugMode)} type={ButtonType.TertiaryBlack} onClick={() => router.navigate("debug/main", true)} /> : undefined }
                <Header.Button icon={IconPeople} iconSize="huge" important={true} id="topbar-user" title={translate(TopBarTranslationsDictionary.User)} type={ButtonType.TertiaryBlack} onClick={() => setUserMobileSheet(true)} />
                { canShowIcons ? <Header.Button iconSize="huge" key="notification" icon={IconNotificationAlarm} important={true} id="topbar-notifications" title={translate(TopBarTranslationsDictionary.Notifications)} type={ButtonType.TertiaryBlack} onClick={() => setNotificationMobileSheet(true)}>{notificationCount > 0 ? <span id="topbar-notification-count">{notificationCount}</span> : undefined}</Header.Button> : undefined }
            </>
        );
    };

    const render = () => {
        let isWhiteLabelWithIcon = window.whitelabel === "shell" || window.whitelabel === "lytx" || window.whitelabel === "kia";
        if (hidingIcons(store) && (!(window.whitelabel) || isWhiteLabelWithIcon)) {
            return renderLoginState();
        }

        return (
            <Header>
                <Header.Title pageName={translate(pageState || "")} >
                    { renderBackButton() }
                </Header.Title>
                { renderActionButton() }
                { renderAdditionalActionButton() }
            </Header>
        );
    };

    return (
        <ThemeDrive dark={nightMode}>
            { render() }
            <BannerAlert setConnectionMobileSheet={setConnectionMobileSheet} setDisplayDetail={setConnectionDisplayDetail} displayMode="singleline" />
            <UserDialogContent userMobileSheet={userMobileSheet} setUserMobileSheet={setUserMobileSheet} />
            <NotificationDialog notificationMobileSheet={notificationMobileSheet} setNotificationMobileSheet={setNotificationMobileSheet} />
            <ConnectionStatusDialog connectionMobileSheet={connectionMobileSheet} setConnectionMobileSheet={setConnectionMobileSheet} displayDetail={connectionDisplayDetail} setDisplayDetail={setConnectionDisplayDetail} />
        </ThemeDrive>

    );
};

export default TopBarViewModules;